import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { types as sdkTypes } from '../../../../../util/sdkLoader';
import css from './SectionSearchBar.module.css';
import { PRIME_SEARCH, SUB_SEARCH } from '../../../../../util/types';
import { useConfiguration } from '../../../../../context/configurationContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { createResourceLocatorString } from '../../../../../util/routes';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { skillTypeMapperAlgolia } from '../../../../../util/constants';
import { Button, NamedLink } from '../../../../../components';

const { LatLngBounds, LatLng } = sdkTypes;
const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const PREDICTIONS_KEY = 'predictions';
const MAX_PREDICTIONS = 3;

// Store prediction to local storage
const storePredictions = prediction => {
  if (!prediction || !prediction.id || typeof window === 'undefined') {
    return;
  }

  const storedPredictions = window.localStorage.getItem(PREDICTIONS_KEY);
  const parsedPredictions = storedPredictions ? JSON.parse(storedPredictions) : [];

  const predictionsMap = new Map(parsedPredictions.map(pred => [pred.id, pred]));
  predictionsMap.delete(prediction.id); // Ensure no duplicates
  const newPredictions = [prediction, ...Array.from(predictionsMap.values())];

  const limitedPredictions = newPredictions.slice(0, MAX_PREDICTIONS);

  window.localStorage.setItem(PREDICTIONS_KEY, JSON.stringify(limitedPredictions));
};

// Get predictions from the local storage
const getPredictions = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storedPredictions = window.localStorage.getItem(PREDICTIONS_KEY);
  return storedPredictions ? JSON.parse(storedPredictions) : [];
};

function SectionSearchBar({ activeTab, setActiveTab, options, isCurrentUserProfessional,
  isCurrentUserClient, params }) {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(''); // New state for skill
  const blurTimeout = useRef(null);
  const dropdownRef = useRef(null);
  const routesConfiguration = useRouteConfiguration();
  const history = useHistory();
  // const [activeTab, setActiveTab] = useState(0);
  const [isDropdown, setIsDropdown] = useState(false);

  const handleTabClick = () => {
    setActiveTab(params.tabs === "project" ? 1 : 0);
  };

  const intl = useIntl();
  const storedPredictions = getPredictions();
  const renderablePredictions =
    predictions && predictions.length > 0
      ? predictions
      : storedPredictions && storedPredictions.length > 0
        ? storedPredictions
        : [];

  const showPredictions = inputFocused;

  const listingFieldsConfig = useConfiguration();
  const optionss = listingFieldsConfig?.['categoryConfiguration']?.['categories'] || [];

  useEffect(() => {
    if (inputValue) {
      fetchPredictions(inputValue);
    } else {
      setPredictions([]);
    }
  }, [inputValue]);

  const fetchPredictions = async query => {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxToken}`
      );
      setPredictions(response.data.features);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const handleInputChange = e => {
    setInputValue(e.target.value);
    setError('');
  };

  const handleInputBlur = () => {
    blurTimeout.current = setTimeout(() => {
      setInputFocused(false);
    }, 500);
  };

  // Function to calculate bounds and origin from a prediction
  const getLocationData = prediction => {
    const { bbox = [], center = [] } = prediction || {};

    const [swLng, swLat, neLng, neLat] = bbox;

    const kmToDegLat = 50 / 111;
    const kmToDegLng = 50 / (111 * Math.cos((((swLat + neLat) / 2) * Math.PI) / 180));

    const expandedBounds =
      bbox && bbox.length === 4
        ? [swLng - kmToDegLng, swLat - kmToDegLat, neLng + kmToDegLng, neLat + kmToDegLat]
        : null;

    const constructedBounds =
      center && center.length === 2
        ? [
          center[0] - kmToDegLng,
          center[1] - kmToDegLat,
          center[0] + kmToDegLng,
          center[1] + kmToDegLat,
        ]
        : null;

    let selectedBounds = expandedBounds
      ? {
        southWest: { lng: expandedBounds[0], lat: expandedBounds[1] },
        northEast: { lng: expandedBounds[2], lat: expandedBounds[3] },
      }
      : constructedBounds
        ? {
          southWest: { lng: constructedBounds[0], lat: constructedBounds[1] },
          northEast: { lng: constructedBounds[2], lat: constructedBounds[3] },
        }
        : null;

    const { southWest, northEast } = selectedBounds || {};
    const swLatLng = new LatLng(southWest?.lat, southWest?.lng);
    const neLatLng = new LatLng(northEast?.lat, northEast?.lng);
    const latlngBounds = new LatLngBounds(neLatLng, swLatLng);

    const latLng = new LatLng(center[1], center[0]);

    return { latlngBounds, latLng };
  };

  const handlePredictionClick = prediction => {
    if (blurTimeout && blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }

    const { latlngBounds, latLng } = getLocationData(prediction);

    setInputValue(prediction.place_name);
    setError('');
    setInputFocused(false);

    const location = {
      address: prediction.place_name,
      bounds: latlngBounds,
      origin: latLng,
    };

    storePredictions(prediction);

    // Handle location selection here (set location in parent or other logic)
    // handleRefineLocation(location);
  };

  const handleFindSubsClick = () => {
    const location = getLocationData(renderablePredictions[0]);

    const { latlngBounds, latLng } = location;

    const skill = selectedSkill; // Skill selected from the dropdown
    if (latlngBounds || skill) {
      const locationData = {
        address: inputValue,
        bounds: latlngBounds,
        origin: latLng,
      };
      handleRefineLocation(locationData, skill);
    }
  };

  const handleRefineLocation = (location, skill) => {
    const queryParams = {
      ...location,
      professionalSkills: skill, // Pass the selected skill as a filter
    };
    const { address } = location || {}
    if (address || skill) {
      history.push(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          { tab: params.tab == "projects" || isCurrentUserProfessional ? PRIME_SEARCH : SUB_SEARCH },
          queryParams
        )
      );
    } else {
      history.push(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          { tab: params.tab == "projects" || isCurrentUserProfessional ? PRIME_SEARCH : SUB_SEARCH }
        )
      )
    }
  };

  return (
    <div className={css.searchBarBox}>
      <div className={css.firstStep}>
        <div className={css.smallTitle}>
          <FormattedMessage id="SectionSearchBar.contractors" />
        </div>
     
        { params.tab == "projects" || isCurrentUserProfessional ? 
        <div className={css.largeText}>
          <FormattedMessage id="SectionSearchBar.manageContractor" />
        </div> : <div className={css.largeText}>
          <FormattedMessage id="SectionSearchBar.subContractor" />
        </div>}
         {/* <div className={css.largeText}>
          <FormattedMessage id="SectionSearchBar.subContractors" />
        </div> */}

        <div className={css.mainWrapper}>
          <div className={css.searchTabs}>
            <div className={css.tabButtons}>
              {isCurrentUserProfessional ? (
                <Button
                  className={activeTab === 1 || isCurrentUserProfessional ? css.active : css.inActive}
                >
                  <FormattedMessage id="SectionSearchBar.projects" />
                </Button>
              ) : isCurrentUserClient ? (
                <Button
                  className={activeTab === 0 || isCurrentUserClient ? css.active : css.inActive}
                >
                  <FormattedMessage id="SectionSearchBar.pros" />
                </Button>
              ) : (
                <div className={css.tabLinkButton}>
                  <NamedLink
                    className={params.tab === "projects" ? css.inActive : css.active}
                    name="LandingPage">
                    <FormattedMessage id="SectionSearchBar.pros" />
                  </NamedLink>
                  <NamedLink
                    className={params.tab === "projects" ? css.active : css.inActive}
                    name="LandingPageProjects" params={{ tab: "projects" }}>
                    <FormattedMessage id="SectionSearchBar.projects" />
                  </NamedLink>
                </div>
              )}
            </div>

          </div>

          <div className={css.mainBox}>
            <div className={css.selectFields}>
              <div className={css.cityName}>
                <input
                  type="text"
                  name="city_name"
                  value={inputValue}
                  onFocus={() => setInputFocused(true)}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className={css.input}
                  placeholder={intl.formatMessage({ id: 'SectionSearchBar.city' })}
                />
                {error && <div className={css.error}>{error}</div>}
                {renderablePredictions.length > 0 && showPredictions && (
                  <ul className={css.cityListBox} ref={dropdownRef}>
                    {renderablePredictions.map(prediction => (
                      <li
                        key={prediction.id}
                        onClick={() => handlePredictionClick(prediction)}
                        className={css.dropdownItem}
                      >
                        {prediction.place_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className={css.selectSkils}>
                <div className={css.skillsInput} onClick={e => setIsDropdown(!isDropdown)}>
                  {selectedSkill ? (
                    Object.entries(skillTypeMapperAlgolia)
                      .filter(([key, value]) => value == selectedSkill)
                      .map(([key]) => key)
                  ) : (
                    <FormattedMessage id="SectionSearchBar.skill" />
                  )}
                </div>
                {isDropdown && (
                  <ul className={css.cityListBox}>
                    {optionss.map(data => (
                      <li
                        className={css.dropdownItem}
                        key={data.id}
                        onClick={() => {
                          setSelectedSkill(skillTypeMapperAlgolia[data.name]);
                          setIsDropdown(!isDropdown);
                        }}
                      >
                        {data.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <button className={css.findButton} onClick={handleFindSubsClick}>
              {params.tab === "projects" || isCurrentUserProfessional ? (
                <FormattedMessage id="SectionSearchBar.projectButton" />
              ) : isCurrentUserClient ? (
                <FormattedMessage id="SectionSearchBar.findSubs" />
              ) : (
                <FormattedMessage id="SectionSearchBar.findSubs" />
              )}
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}

export default SectionSearchBar;
